import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Send, Edit3, Trash2, ExternalLink, MessageSquare } from "lucide-react";

const STATES = ["NSW", "QLD", "VIC", "WA", "SA", "TAS", "NT", "ACT", "ALL"];

/**
 * AdminQAGeneratorWidget. admin tool for the "paste an FB question → publish
 * a polished /q page" workflow.
 *
 * Flow:
 * 1. Founder pastes the question they saw in a Facebook group (anonymise PII
 * first. the tool doesn't do that automatically; it's a human curation
 * step).
 * 2. Click Generate. Compass produces a draft Q&A with title/teaser/answer/
 * slug/citations.
 * 3. Founder reviews and (if needed) edits any field inline.
 * 4. Click Publish. Entry is saved to db.demo_qa and instantly live at
 * /q/<slug> for SEO + sharing.
 *
 * Hides itself for non-admins (403 probe on /admin/qa/list).
 */
export default function AdminQAGeneratorWidget() {
 const [allowed, setAllowed] = useState(null);
 const [items, setItems] = useState([]);
 // Form state
 const [question, setQuestion] = useState("");
 const [state, setState] = useState("NSW");
 const [persona, setPersona] = useState("");
 const [busy, setBusy] = useState(false);
 // Draft (after generate)
 const [draft, setDraft] = useState(null);

 const loadItems = async () => {
 try {
 const { data } = await api.get("/admin/qa/list");
 setItems(data.items || []);
 setAllowed(true);
 } catch (e) {
 if (e.response?.status === 403) setAllowed(false);
 else { console.warn("Q&A list probe failed:", e); setAllowed(false); }
 }
 };

 useEffect(() => { loadItems(); }, []);

 if (!allowed) return null;

 const generate = async (e) => {
 e?.preventDefault();
 if (!question.trim()) {
 toast.error("Paste a question first.");
 return;
 }
 setBusy(true); setDraft(null);
 try {
 const { data } = await api.post("/admin/qa/generate", {
 question: question.trim(),
 state,
 persona: persona.trim() || null,
 });
 setDraft({
 slug: data.suggested_slug,
 title: data.title,
 teaser: data.teaser,
 meta_description: data.meta_description,
 persona: data.persona,
 question: question.trim(),
 answer: data.answer,
 citations: Array.isArray(data.citations) ? data.citations : [],
 state: data.state || state,
 });
 toast.success("Draft generated. review and publish below.");
 } catch (err) {
 toast.error(err.response?.data?.detail || "Generation failed. check the question and try again.");
 } finally {
 setBusy(false);
 }
 };

 const publish = async () => {
 if (!draft?.slug || !draft?.title) {
 toast.error("Slug and title are required.");
 return;
 }
 setBusy(true);
 try {
 const { data } = await api.post("/admin/qa/publish", draft);
 toast.success("Published. live now", {
 description: `tenanttrail.net${data.url}`,
 action: { label: "View", onClick: () => window.open(data.url, "_blank") },
 duration: 9000,
 });
 setDraft(null);
 setQuestion("");
 setPersona("");
 loadItems();
 } catch (err) {
 toast.error(err.response?.data?.detail || "Publish failed.");
 } finally {
 setBusy(false);
 }
 };

 const remove = async (slug) => {
 if (!window.confirm(`Delete /q/${slug}? This cannot be undone.`)) return;
 try {
 await api.delete(`/admin/qa/${slug}`);
 toast.success(`Deleted /q/${slug}`);
 loadItems();
 } catch (err) {
 toast.error(err.response?.data?.detail || "Delete failed.");
 }
 };

 return (
 <div className="card mb-6 border-l-4 border-l-[#1B6C62]" data-testid="admin-qa-generator-widget">
 <div className="flex items-center gap-2 mb-1">
 <MessageSquare size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">Generate a Q&amp;A from a Facebook question</h3>
 <span className="label-mono text-[10px] text-[#1B6C62]">Admin only</span>
 </div>
 <p className="text-xs text-[#6B7280] mb-4 italic">
 Paste a question from a tenant Facebook group. Compass drafts a publishable answer. Review, tweak, hit Publish. It&apos;s live at /q/&lt;slug&gt; in 5 seconds. Anonymise PII before pasting.
 </p>

 {/* Input form */}
 <form onSubmit={generate} className="space-y-3 mb-4">
 <div>
 <label className="label-mono mb-1 block">Question (paste from FB)</label>
 <textarea
 value={question}
 onChange={e => setQuestion(e.target.value)}
 placeholder="e.g. My landlord refuses to fix the leaking shower in WA, what can I do?"
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62] resize-y min-h-[80px] font-serif"
 data-testid="qa-gen-question"
 required
 />
 </div>
 <div className="grid sm:grid-cols-[120px_1fr] gap-2">
 <div>
 <label className="label-mono mb-1 block">State</label>
 <select
 value={state}
 onChange={e => setState(e.target.value)}
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid="qa-gen-state"
 >
 {STATES.map(s => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 <div>
 <label className="label-mono mb-1 block">Persona (optional)</label>
 <input
 value={persona}
 onChange={e => setPersona(e.target.value)}
 placeholder="e.g. Single mum, 6-month lease, regional NSW"
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid="qa-gen-persona"
 />
 </div>
 </div>
 <button
 type="submit"
 disabled={busy}
 className="btn-primary inline-flex items-center gap-2 text-sm"
 data-testid="qa-gen-submit"
 >
 {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
 Generate draft
 </button>
 </form>

 {/* Draft preview / edit */}
 {draft && (
 <div className="border border-[#1B6C62]/40 bg-[#E6F5F2]/30 p-4 mb-4" data-testid="qa-gen-draft">
 <div className="flex items-center gap-2 mb-3">
 <Edit3 size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#1B6C62]">Draft. Edit any field before publishing</span>
 </div>
 <div className="space-y-3 text-sm">
 <Field label="Slug (URL: /q/<slug>)" value={draft.slug} onChange={v => setDraft({...draft, slug: v })} dt="qa-gen-slug" />
 <Field label="Title" value={draft.title} onChange={v => setDraft({...draft, title: v })} dt="qa-gen-title" />
 <Field label="Teaser (1 sentence)" value={draft.teaser} onChange={v => setDraft({...draft, teaser: v })} dt="qa-gen-teaser" />
 <Field label="Meta description (SEO)" value={draft.meta_description} onChange={v => setDraft({...draft, meta_description: v })} dt="qa-gen-meta" />
 <Field label="Persona" value={draft.persona} onChange={v => setDraft({...draft, persona: v })} dt="qa-gen-persona-edit" />
 <div>
 <label className="label-mono mb-1 block">Answer (markdown. ## headings, **bold**, &gt; quotes, - bullets)</label>
 <textarea
 value={draft.answer}
 onChange={e => setDraft({...draft, answer: e.target.value })}
 className="w-full border border-[#E5E4DE] px-3 py-2 text-xs focus:outline-none focus:border-[#1B6C62] resize-y min-h-[220px] font-mono"
 data-testid="qa-gen-answer"
 />
 </div>
 <Field
 label="Citations (comma-separated)"
 value={draft.citations.join(", ")}
 onChange={v => setDraft({...draft, citations: v.split(",").map(s => s.trim()).filter(Boolean) })}
 dt="qa-gen-citations"
 />
 </div>
 <div className="flex justify-end gap-2 mt-4">
 <button
 onClick={() => setDraft(null)}
 className="btn-ghost text-sm px-3 py-1.5"
 data-testid="qa-gen-discard"
 >Discard</button>
 <button
 onClick={publish}
 disabled={busy}
 className="btn-primary inline-flex items-center gap-1.5 text-sm px-3 py-1.5"
 data-testid="qa-gen-publish"
 >
 {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Send size={14} strokeWidth={1.5} />}
 Publish to /q/{draft.slug || "<slug>"}
 </button>
 </div>
 </div>
 )}

 {/* Existing items */}
 {items.length > 0 && (
 <div>
 <div className="label-mono mb-2">Published Q&amp;As ({items.length})</div>
 <div className="space-y-1 max-h-60 overflow-y-auto">
 {items.map((q, i) => (
 <div key={q.slug} className="flex justify-between gap-2 text-xs border-b border-[#E5E4DE] py-1.5 items-center" data-testid={`qa-row-${q.slug}`}>
 <Link to={`/q/${q.slug}`} target="_blank" className="text-[#0D2B52] hover:text-[#1B6C62] truncate flex-1 font-serif text-[13px] inline-flex items-center gap-1">
 {q.title} <ExternalLink size={10} strokeWidth={1.75} className="text-[#1B6C62] flex-shrink-0" />
 </Link>
 <span className="text-[10px] text-[#1B6C62] font-mono flex-shrink-0">{q.state}</span>
 <button onClick={() => remove(q.slug)} className="btn-ghost text-[#B35D4C] p-1" title="Delete">
 <Trash2 size={12} strokeWidth={1.5} />
 </button>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 );
}

function Field({ label, value, onChange, dt }) {
 return (
 <div>
 <label className="label-mono mb-1 block">{label}</label>
 <input
 value={value || ""}
 onChange={e => onChange(e.target.value)}
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid={dt}
 />
 </div>
 );
}
